import { useEffect, useState } from 'react';
import { ListBulletIcon, MapIcon } from '@heroicons/react/24/outline';
import Meta from './Meta';
import HomeFooter from './HomeFooter';
import Content from './Content';
import ShowsMapView from './ShowsMapView';
import ProfilePhoto from './ProfilePhoto';
import PageHeader from './shell/PageHeader';
import { Link } from '../util/router';
import { getUser } from '../util/util';

function UserShowsPage({
  userId,
  heading,
  subtitle,
  shows,
  loading,
  empty,
  allowMap = true,
  beforeShows,
  onSeenToggle,
  renderExtra,
  source,
}) {
  const [user, setUser] = useState(null);
  const [view, setView] = useState('list');

  useEffect(() => {
    if (!userId) return;
    getUser(userId).then(setUser).catch(console.error);
  }, [userId]);

  const title = user ? `@${user.username}'s ${heading}` : heading;

  const toggle = allowMap && shows.length > 0 && (
    <div className="flex rounded-full border border-black/20 bg-white p-1 shadow-sm">
      {[
        { value: 'list', label: 'List', Icon: ListBulletIcon },
        { value: 'map', label: 'Map', Icon: MapIcon },
      ].map(({ value, label, Icon }) => (
        <button
          key={value}
          type="button"
          onClick={() => setView(value)}
          aria-pressed={view === value}
          className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm ${
            view === value ? 'bg-black text-white' : 'text-slate-700 hover:bg-slate-100'
          }`}
        >
          <Icon className="h-4 w-4" />
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <>
      <Meta title={`${heading} - Showrunner`} />
      <PageHeader back title={title} subtitle={subtitle} actions={toggle}>
        {user && (
          <Link
            to={`/profile/${user.username}`}
            className="inline-flex items-center gap-2 text-sm text-black/60 hover:text-black"
          >
            <ProfilePhoto userId={userId} isSettings={false} small />
            {user.name || `@${user.username}`}
          </Link>
        )}
      </PageHeader>

      <section className="mx-auto min-h-[60vh] w-full max-w-[90rem] py-6 md:py-8">
        {beforeShows}
        {loading ? (
          <div className="flex justify-center py-16">
            <span className="loading loading-spinner loading-lg text-secondary-red" />
          </div>
        ) : shows.length === 0 ? (
          <div className="px-4 py-16 text-center text-lg text-slate-600 md:px-8">{empty}</div>
        ) : view === 'map' ? (
          <div className="px-4 md:px-8">
            <ShowsMapView shows={shows} />
          </div>
        ) : (
          <Content
            shows={shows}
            compact
            source={source}
            onSeenToggle={onSeenToggle}
            renderExtra={renderExtra}
          />
        )}
      </section>
      <HomeFooter />
    </>
  );
}

export default UserShowsPage;
